import { FormControl, FormGroup, Validators } from "@angular/forms";
import {
  BmExpensesType,
  BmExpGroup, BmItem,
  BmMgmFeeGroup,
  BudgetModel,
  BudgetModelInvestment,
} from "../../pojo/bm-classes";
import {
  BmMgmFeeGroupForm,
  BmModelDataFg,
  BudgetModelFormGroup,
} from "../../pojo/bm-form";
import { BmSubscriptionsService } from "./subscription/bm-subscriptions.service";
import { BmInvestmentFormService } from "../investment/bm-investment-form.service";
import { BmGrandTotalMgmFeeCalcService } from "../mgmFee/total/bm-grand-total-mgm-fee-calc.service";
import { BmExpensesCalcService } from "../expenses/bm-expenses-calc.service";
import { BmTotalAccumulatedCapCalcService } from "../total/accumulated/bm-total-accumulated-cap-calc.service";
import { BmMgmGeneralCalcService } from "../mgmFee/general/bm-mgm-general-calc.service";
import { BmExpensesFormService } from "../expenses/bm-expenses-form.service";
import { BmYearQuarterService } from "./bm-year-quarter.service";
import { BmTotalGeneralCalcService } from "../total/general/BmTotalGeneralCalcService";
import { MAX_MODEL_YEARS_NUMBER, ValidatorMustStayDisabled, ValidatorNotBlank } from "../../pojo/bm-const";
import { BmAnalyticsFormService } from "../../analytics/main/service/bm-analytics-form.service";
import { BmSubscriptionsInvestmentHelperService } from "./subscription/bm-subscriptions-investment-helper.service";
import { OrganizationDTO } from "../../../../organization/dto/OrganizationDTO";

export class BudgetFormService {

  /**
   * Create Model Form group. Calculate totals, make subscriptions.
   *
   * @param model data, saved on BackEnd (in edit case) or new model data (in create case)
   * @param org selected Organization
   * @param isNewModel flag to mark new model creation with edit state enabled
   */
  public static createBudgetForm(model: BudgetModel, org: OrganizationDTO, isNewModel: boolean): BudgetModelFormGroup {
    const budgetForm = new FormGroup({
      uuid: new FormControl<string>(model.uuid),
      orgUuid: new FormControl<string>(org.uuid),
      name: new FormControl<string>(model.name, { validators: [ Validators.required, ValidatorNotBlank] }),
      isEditState: new FormControl<boolean>(isNewModel),
      totalCommitment: new FormControl<number>({ value: model.totalCommitment, disabled: true }, { validators: ValidatorMustStayDisabled }),
      previousTotalCommitment: new FormControl<number>(model.totalCommitment),
      unallocatedCommitment: new FormControl<number>({ value: null, disabled: true }),
      yearsNumber: new FormControl<number>(model.yearsNumber),
      tempYearsNumber: new FormControl<number>({ value: model.yearsNumber, disabled: true }, { validators: [Validators.max(MAX_MODEL_YEARS_NUMBER), ValidatorMustStayDisabled] }),
      previousYearsNumber: new FormControl<number>(model.yearsNumber),
      lastModifiedDate: new FormControl<Date>(model.lastModifiedDate),
      createdDate: new FormControl<Date>(model.createdDate),
      createdBy: new FormControl<string>(model.createdBy),
      modelHeaders: BmYearQuarterService.createModelHeaders(model.yearsNumber),
      isAllQuartersCollapsed: new FormControl<boolean>(false),
      collapsedYears: new FormControl<number[]>([]),
      modelData: new FormGroup<BmModelDataFg>({
        mgmFee: BmMgmGeneralCalcService.createForm(model.modelData.mgmFee, model.yearsNumber),
        operatingExpenses: BmExpensesFormService.createForm(model.modelData.operatingExpenses, model.yearsNumber),
        organizationalExpenses: BmExpensesFormService.createForm(model.modelData.organizationalExpenses, model.yearsNumber),
        investment: BmInvestmentFormService.createForm(model.modelData.investment),
        totalExpenses: BmTotalGeneralCalcService.createForm(model.yearsNumber),
        totalCapRequired: BmTotalGeneralCalcService.createForm(model.yearsNumber),
        totalCapAccumulated: BmTotalGeneralCalcService.createForm(model.yearsNumber),
      }),
      analyticsData: BmAnalyticsFormService.createForm(),
    });

    BmGrandTotalMgmFeeCalcService.recalculateGrandTotal(budgetForm.controls.modelData.controls.mgmFee);
    BmExpensesCalcService.recalculateSectionTotal(budgetForm.controls.modelData.controls.operatingExpenses);
    BmExpensesCalcService.recalculateSectionTotal(budgetForm.controls.modelData.controls.organizationalExpenses);

    BmTotalGeneralCalcService.recalculateTotalExpenses(budgetForm, budgetForm.controls.modelData.controls.totalExpenses, false);

    BmSubscriptionsInvestmentHelperService.recalculateInvestmentValue(budgetForm);
    BmInvestmentFormService.recalculateInvestment(budgetForm.controls.modelData.controls.investment);

    BmTotalGeneralCalcService.recalculateTotalExpenses(budgetForm, budgetForm.controls.modelData.controls.totalCapRequired, true);

    BmTotalGeneralCalcService.recalculateAllRowValuePcnt(budgetForm);
    BmTotalAccumulatedCapCalcService.recalculateTotalAccumulatedRequired(budgetForm);
    BmSubscriptionsService.subscribeOnTotalCommitmentChanges(budgetForm);
    BmSubscriptionsService.subscribeOnSectionChanges(budgetForm);

    return budgetForm;
  }


  public static mapFormToDto(form: BudgetModelFormGroup): BudgetModel {
    return {
      uuid: form.getRawValue().uuid,
      orgUuid: form.getRawValue().orgUuid,
      name: form.getRawValue().name,
      totalCommitment: Number(form.getRawValue().totalCommitment),
      yearsNumber: Number(form.getRawValue().yearsNumber),
      lastModifiedDate: form.getRawValue().lastModifiedDate,
      createdDate: form.getRawValue().createdDate,
      createdBy: form.getRawValue().createdBy,
      modelData: {
        mgmFee: this.mapMgmFeeGroups(form),
        operatingExpenses: this.mapExpGroup(form, 'operatingExpenses'),
        organizationalExpenses: this.mapExpGroup(form, 'organizationalExpenses'),
        investment: this.mapInvestment(form),
      },
    };
  }

  public static mapMgmFeeGroups(form: BudgetModelFormGroup): BmMgmFeeGroup[] {
    const groups = form.controls.modelData.controls.mgmFee.controls.groups.controls;

    return groups.map((group) => this.mapMgmFeeGroup(group));
  }

  public static mapMgmFeeGroup(group: FormGroup<BmMgmFeeGroupForm>): BmMgmFeeGroup {
    const sg = group.getRawValue().subGroups;

    return <BmMgmFeeGroup>{
      uuid: group.getRawValue().uuid,
      name: group.getRawValue().name,
      priority: group.getRawValue().priority,
      subGroups: {
        commitment: {
          standardValue: Number(sg.commitment.standardValue),
          items: this.mapItems(sg.commitment.items),
        },
        mgmFeesPcnt: {
          standardValue: Number(sg.mgmFeesPcnt.standardValue),
          items: this.mapItems(sg.mgmFeesPcnt.items),
        },
        pcntOfMgmFees: {
          isSelected: sg.pcntOfMgmFees.isSelected,
          standardValue: Number(sg.pcntOfMgmFees.standardValue),
          items: this.mapItems(sg.pcntOfMgmFees.items),
        },
        vatPcnt: {
          isSelected: sg.vatPcnt.isSelected,
          standardValue: Number(sg.vatPcnt.standardValue),
          items: this.mapItems(sg.vatPcnt.items),
        },
        isCatchUpSelected: sg.catchUp.isSelected,
      },
    };
  }


  private static mapExpGroup(form: BudgetModelFormGroup, section: BmExpensesType): BmExpGroup[] {

    const groups = form.controls.modelData.controls[section].controls.groups.controls;

    return groups.map((group) => {
      const rowValue = group.getRawValue();
      return <BmExpGroup>{
        uuid: rowValue.uuid,
        name: rowValue.name,
        priority: rowValue.priority,
        dataFillingType: rowValue.dataFillingType,
        standardValue: Number(rowValue.standardValue),
        items: this.mapItems(rowValue.items),
      };
    });
  }

  private static mapInvestment(form: BudgetModelFormGroup): BudgetModelInvestment {
    const rowValue = form.controls.modelData.controls.investment.getRawValue();

    return <BudgetModelInvestment>{
      uuid: rowValue.uuid,
      investmentValue: Number(rowValue.investmentValue),
      investmentValueCalcType: rowValue.investmentValueCalcType,
      itemsPcnt: this.mapItems(rowValue.itemsPcnt),
      itemsValue: this.mapItems(rowValue.itemsValue),
    };
  }

  private static mapItems(items: BmItem[]): BmItem[] {
    return items.map((item) => ({
      ...item,
      itemValue: Number(item.itemValue || 0),
    }));
  }
}
